import { ReactNode, useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { History } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import CustomDataGrid from '../../ui/customDataGrid/customDataGrid.component';
import { DateRangeValue } from '../../ui/dateRangeFilter/dateRangeFilter.component';
import { JOB_TASKS_ENDPOINT } from '../../../consts';
import { useJobTaskServiceHistoryColumns } from '../../../workspaces/service/jobs/jobTaskServiceHistoryColumns';

interface EquipmentServiceHistoryGridProps {
  equipmentId: string | number | null | undefined;
  title: string;
  /** Narrows the history to the same [start_date, end_date] window the
   * statistics cards use - omitted where the table shows the whole history. */
  dateRange?: DateRangeValue;
  gridSettingsKey?: string;
  refreshEventName?: string | string[];
  /** "card" renders the grid inside a titled Card, the way the statistics
   * pages lay their tables out; "page" gives it the grid's own header bar. */
  variant?: 'page' | 'card';
}

/**
 * The service history of one piece of equipment: every JobTask recorded against
 * it, newest first, with the job it belongs to, its relevant date, issues,
 * services and used parts. A row opens the job it belongs to.
 *
 * One component (and one endpoint) behind the equipment view, the equipment
 * statistics page and the job view, so the three tables cannot drift apart.
 */
export default function EquipmentServiceHistoryGrid({
  equipmentId,
  title,
  dateRange,
  gridSettingsKey,
  refreshEventName,
  variant = 'page',
}: EquipmentServiceHistoryGridProps) {
  const navigate = useNavigate();
  const columns = useJobTaskServiceHistoryColumns();

  // Memoized: CustomDataGrid refetches whenever this object's identity changes.
  const extraParams = useMemo(
    () => ({
      equipment: equipmentId ?? undefined,
      start_date: dateRange?.start_date,
      end_date: dateRange?.end_date,
    }),
    [equipmentId, dateRange?.start_date, dateRange?.end_date]
  );
  const scopeKey = `${equipmentId}-${dateRange?.start_date ?? ''}-${dateRange?.end_date ?? ''}`;

  const handleRowClick = (_jobTaskId: string, row?: { job?: { id?: string } }) => {
    if (row?.job?.id) {
      navigate(`/service/jobs/${row.job.id}`);
    }
  };

  const grid: ReactNode = (
    <CustomDataGrid
      key={scopeKey}
      icon={variant === 'page' ? <History /> : undefined}
      title={variant === 'page' ? title : undefined}
      url={JOB_TASKS_ENDPOINT}
      gridSettingsKey={gridSettingsKey}
      columns={columns}
      extraParams={extraParams}
      onRowClick={handleRowClick}
      refreshEventName={refreshEventName}
      dataGridProps={
        variant === 'card'
          ? { autoHeight: true, getRowHeight: () => 'auto', pageSizeOptions: [5, 10, 25] }
          : { getRowHeight: () => 'auto' }
      }
    />
  );

  if (variant === 'card') {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {title}
          </Typography>
          {grid}
        </CardContent>
      </Card>
    );
  }

  return <Box sx={{ height: '100%', width: '100%' }}>{grid}</Box>;
}
